import { ReactElement } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/base_components/ui/button'
import { dateFormats } from 'constants/common'
import { getAbbrForTimeZone } from './utils'

type PrimaryBookButtonProps = {
  slot?: ProviderSlot | null
  format: (date: string | number, formatStr: string) => string
  timeZone: string
  onBook: () => void
  isSlotSelected?: boolean
  isLoading?: boolean
}

const PrimaryBookButton = ({
  slot,
  format,
  timeZone,
  onBook,
  isLoading = false,
}: PrimaryBookButtonProps): ReactElement | null => {
  if (!slot && !isLoading) {
    return null
  }

  const slotIsToday =
    !!slot &&
    format(slot.start, 'yyyy-MM-dd') === format(Date.now(), 'yyyy-MM-dd')

  const timeZoneAbbr = slot
    ? getAbbrForTimeZone(new Date(slot.start), timeZone)
    : ''

  const formattedTime = slot
    ? `${format(slot.start, dateFormats.TIME_FORMAT_LOWER).replace(
        ':00',
        ''
      )} ${timeZoneAbbr}`
    : ''

  const formattedDateTime = !slot
    ? ''
    : slotIsToday
    ? `Today, ${formattedTime}`
    : `${format(
        slot.start,
        `${dateFormats.DAY_FORMAT} ${dateFormats.MONTH_DAY_FORMAT}`
      )} ${formattedTime}`

  const labelPrefix = 'Book session:'

  const ariaLabel = `Book session on ${formattedDateTime}`

  return (
    <Button
      className="~font-gibson ~text-base ~not-italic disabled:~opacity-100"
      fullWidth
      aria-label={isLoading ? 'Loading session' : ariaLabel}
      onClick={isLoading ? undefined : onBook}
      disabled={isLoading}
    >
      {isLoading || !slot ? (
        <Loader2 className="~h-5 ~w-5 ~animate-spin ~text-navy" />
      ) : (
        <span className="~block ~overflow-hidden ~text-ellipsis ~whitespace-nowrap">
          <span className="~font-semibold">{labelPrefix}</span>{' '}
          <span className="~font-normal">{formattedDateTime}</span>
        </span>
      )}
    </Button>
  )
}

export default PrimaryBookButton
